import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase, supabaseReady } from "@/lib/supabaseClient";
import type { User } from "@supabase/supabase-js";

type Props = {
  children: React.ReactNode;
  redirectTo?: string;
  // Opcional: callback para fornecer o usuário ao child via contexto
  onUserResolved?: (user: User | null) => void;
};

export function ProtectedRoute({ children, redirectTo = "/login", onUserResolved }: Props) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      if (!supabaseReady) {
        if (mounted) {
          setAuthenticated(false);
          setLoading(false);
        }
        return;
      }

      const { data, error } = await supabase.auth.getSession();
      if (!mounted) return;

      if (error) {
        console.error("[ProtectedRoute] getSession error:", error);
        setAuthenticated(false);
        setLoading(false);
        return;
      }

      const user = data.session?.user ?? null;
      setAuthenticated(!!user);
      setLoading(false);
      onUserResolved?.(user);
    }

    checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      const user = session?.user ?? null;
      setAuthenticated(!!user);
      onUserResolved?.(user);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [onUserResolved]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Verificando autenticação...</p>
      </div>
    );
  }

  if (!authenticated) {
    console.log("[PROTECTED_ROUTE] Not authenticated, redirecting to:", redirectTo);
    console.log("[REDIRECT]", redirectTo);
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
